import { useFormik } from "formik";
import { useState, useEffect, useCallback, useMemo } from "react";
import Button from "src/components/common/Button";
import Input, { InputTypeEnum } from "src/components/common/Input";
import { ModifierItemFormProps } from "src/interfaces/modifier-items-props.interface";
import FormRowVertical from "src/components/common/FormRowVertical";
import ModifierItemOptionsList from "./ModifierItemOptionsList";
import { useAddModifierItem } from "../hooks/useAddModifierItem";
import { useParams } from "react-router-dom";
import { useUpdateModifierItem } from "../hooks/useUpdateModifierItem";
import { ModifierItemOption } from "src/interfaces/modifier.interface";
import { ICONS } from "src/assests";
import { convertCentsToDollars, convertDollarToCents } from "src/helpers";
import { modifierItemSchema } from "../modifierItem.schema";
import MenuItemLocaleFields from "src/components/common/MenuItemLocaleFields";
import { useStorePosLocales } from "src/hooks/useStorePosLocales";
import {
  buildLocaleTranslationMap,
  buildTranslationsPayload,
  LocaleTranslationMap,
  syncLegacyNameFieldsFromLocaleTranslations,
} from "src/utils/localeTranslations";

const ModifierItemForm = ({ modifierItemToEdit, handleCloseModifierItemModal }: ModifierItemFormProps) => {
  const [modifierItemOptions, setModifierItemOptions] = useState<ModifierItemOption[]>([]);
  const { enabledLocaleOptions, enabledLocales, defaultLocale } = useStorePosLocales();

  const initialLocaleTranslations = useMemo(
    () =>
      buildLocaleTranslationMap(modifierItemToEdit?.translations, enabledLocales, {
        englishName: modifierItemToEdit?.name,
        nonEnglishName: modifierItemToEdit?.nonEnglishName,
      }),
    [modifierItemToEdit, enabledLocales]
  );
  const [localeTranslations, setLocaleTranslations] = useState<LocaleTranslationMap>(initialLocaleTranslations);

  useEffect(() => {
    setLocaleTranslations(initialLocaleTranslations);
  }, [initialLocaleTranslations]);

 const { addModifierItem, isAddingModifierItem }  = useAddModifierItem();
 const { updateModifierItem, isUpdatingModifierItem } = useUpdateModifierItem();
 const { modifierCategoryCorrelationId } = useParams();
  
  const buttonText = modifierItemToEdit ? "Update" : "Save";
  const isLoadingButton = modifierItemToEdit ? isUpdatingModifierItem : isAddingModifierItem;

  const formik = useFormik({
    initialValues: {
      name: modifierItemToEdit?.name || "",
      nonEnglishName: modifierItemToEdit?.nonEnglishName || "",
      taxRate: modifierItemToEdit?.taxRate ?? "",
      isActive: modifierItemToEdit ? modifierItemToEdit.active !== false : true,
      modifierItemOptionInputList:
      modifierItemToEdit?.modifierItemOptions?.map((modifierItemOption) => ({
        correlationId: modifierItemOption.correlationId,
        name: modifierItemOption.name || "",
        nonEnglishName: modifierItemOption.nonEnglishName || "",
        priceCents: convertCentsToDollars(modifierItemOption.priceCents),
        renderSequence: modifierItemOption.renderSequence,
      })) || [],
    },
    validationSchema: modifierItemSchema,
    onSubmit: async (values, { resetForm }) => {
      const legacy = syncLegacyNameFieldsFromLocaleTranslations(localeTranslations);
      const translations = buildTranslationsPayload(localeTranslations, enabledLocales);

      if (modifierItemToEdit) {
        const taxRateValue = values.taxRate && values.taxRate !== "" && !isNaN(Number(values.taxRate))
          ? Number(values.taxRate)
          : undefined;

        await updateModifierItem({
          modifierItemCorrelationId: modifierItemToEdit.correlationId || "",
          name: legacy.name,
          nonEnglishName: legacy.nonEnglishName || undefined,
          taxRate: taxRateValue,
          isActive: values.isActive,
          translations,
        });
      } else {
        const taxRateValue = values.taxRate && values.taxRate !== "" && !isNaN(Number(values.taxRate))
          ? Number(values.taxRate)
          : undefined;

        const addInput: any = {
          modifierCategoryCorrelationId: modifierCategoryCorrelationId || "",
          name: legacy.name,
          nonEnglishName: legacy.nonEnglishName || undefined,
          translations,
          modifierItemOptionInputList: values.modifierItemOptionInputList
          .filter((modifierItemOption) => modifierItemOption.name.trim() !== "")
          .map((modifierItemOption) => ({
            ...modifierItemOption,
            priceCents: convertDollarToCents(Number(modifierItemOption.priceCents) || 0),
            renderSequence: Number(modifierItemOption.renderSequence) || 0,
          })),
        };
        if (taxRateValue !== undefined) {
          addInput.taxRate = taxRateValue;
        }
        await addModifierItem(addInput);
      }
      handleCloseModifierItemModal();
      resetForm();
    },
    enableReinitialize: true,
  });
  
  const {setFieldValue, values, touched, errors, handleBlur, handleSubmit} = formik

  const handleAddModifierItemOption = useCallback(() => {
    setModifierItemOptions(prevModifierItemOptions => [
      ...prevModifierItemOptions,
      { name: "", nonEnglishName: "", priceCents: 0, renderSequence: ""},
    ]);
  }, []);

  const handleRemoveModifierItemOption = useCallback((modifierItemOptionIndex: number) => {
    setModifierItemOptions(prevModifierItemOptions => {
      if (prevModifierItemOptions.length > 0) {
        return prevModifierItemOptions.filter((_, prevModifierItemOptionIndex) => prevModifierItemOptionIndex !== modifierItemOptionIndex);
      }
      return prevModifierItemOptions;
    });
  }, []);

  const handleTaxRateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      let taxRate = e.target.value;
      if (taxRate === "") {
        setFieldValue("taxRate", "", true);
        return;
      }
      if (taxRate.includes(".")) {
        const [intPart, decimalPart] = taxRate.split(".");
        if (decimalPart.length > 4) {
          taxRate = `${intPart}.${decimalPart.slice(0, 4)}`;
        }
      }
  
      setFieldValue("taxRate", Number(taxRate), true);
    },
    [setFieldValue]
  );

  const handleUpdateModifierItemOption = useCallback((
    modifierItemOptionIndex: number,
    inputFieldName: "name" | "nonEnglishName" | "priceCents" | "renderSequence",
    inputFieldValue: string | number
  ) => {
    setModifierItemOptions(prevModifierItemOptions => {
      const modifierItemOptions = [...prevModifierItemOptions];
      modifierItemOptions[modifierItemOptionIndex] = {
        ...modifierItemOptions[modifierItemOptionIndex],
        [inputFieldName]: inputFieldValue,
      };
      return modifierItemOptions;
    });
  }, []);
  
    useEffect(() => {
      setFieldValue("modifierItemOptionInputList", modifierItemOptions);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modifierItemOptions]);

  useEffect(() => {
    if (
      modifierItemToEdit?.modifierItemOptions &&
      modifierItemToEdit.modifierItemOptions.length > 0
    ) {
      const initialModifierItemOptions = modifierItemToEdit.modifierItemOptions.map(
        (modifierItemOption) => ({
          correlationId: modifierItemOption.correlationId,
          name: modifierItemOption.name || "",
          nonEnglishName: modifierItemOption.nonEnglishName || "",
          priceCents: convertCentsToDollars(modifierItemOption.priceCents).toFixed(2) ?? 0,
          renderSequence: modifierItemOption.renderSequence,
        })
      );
      setModifierItemOptions(initialModifierItemOptions);
    } else {
      setModifierItemOptions([]);
    }
  }, [modifierItemToEdit]);

  const defaultLocaleName = localeTranslations[defaultLocale]?.name?.trim() ?? "";
  const nameError = !defaultLocaleName ? "Name is Required" : undefined;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
      <MenuItemLocaleFields
        enabledLocaleOptions={enabledLocaleOptions}
        defaultLocale={defaultLocale}
        localeTranslations={localeTranslations}
        onChange={setLocaleTranslations}
        nameError={nameError}
        entityLabel="Modifier item"
        showDescription={false}
      />
      <div className="mb-4 mt-4">
        <FormRowVertical
          label="Optional Tax Rate (Decimal)"
          error={
            touched.taxRate && errors.taxRate
              ? errors.taxRate
              : ""
          }
        >
          <Input
            name="taxRate"
            type={InputTypeEnum.NUMBER}
            min="0"
            max="1"
            step="0.0001"
            value={values.taxRate}
            onChange={handleTaxRateChange}
            onBlur={handleBlur}
          />
        </FormRowVertical>
      </div>
      {modifierItemToEdit ? (
        <div className="mb-4">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={formik.values.isActive !== false}
              onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
              className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none"
            />
            <span className="text-sm">Modifier item is active</span>
          </label>
        </div>
      ) : null}
      <ModifierItemOptionsList
        modifierItemOptions={modifierItemOptions}
        onRemoveModifierItemOptionFromUI={handleRemoveModifierItemOption}
        onUpdateModifierItemOption={handleUpdateModifierItemOption}
        modifierItemCorrelationId={modifierItemToEdit?.correlationId}
        hasModifierItemToEdit={!!modifierItemToEdit}
      />

      <div
        className="mt-4 flex h-[86px] cursor-pointer items-center justify-center rounded-2xl border border-dashed border-[#EAEAEB] transition-colors hover:border-[#D1D5DB]"
        onClick={handleAddModifierItemOption}
      >
        <div className="flex items-center gap-4">
          <img
            src={ICONS.PlusIcon}
            className="h-[16px] w-[16px]"
            alt="Add Option Icon"
          />
          <div className="text-[18px] font-[300] text-[#7B7B7B]">
            Add New Option
          </div>
        </div>
      </div>
      </div>

      <div className="mt-4 flex shrink-0 justify-end border-t border-[#F1F1F2] pt-4">
        <Button type="submit" isLoading={isLoadingButton} disabled={!defaultLocaleName} onClick={() => handleSubmit()}>
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

export default ModifierItemForm;
